import { useLingui } from '@lingui/react/macro';
import { ArrowDown2 } from 'iconsax-reactjs';
import {
  type Dispatch,
  type ReactNode,
  type RefObject,
  type SetStateAction,
  useEffect,
  useState,
} from 'react';
import { Button } from './Button';
import { Dialog } from './Dialog';
import { PopoverContent, PopoverRoot, PopoverTrigger } from './Popover';
import { Ripple } from './Ripple';
import { cn, useIsDesktop } from './react';
import { Spinner } from './Spinner';

export type PickerRef = [boolean, Dispatch<SetStateAction<boolean>>];

export type PickerProps = {
  className?: string;
  modal?: ReactNode;
  root?: ReactNode;
  block?: boolean;
  size: 12 | 10 | 8;
  error?: boolean;
  disabled?: boolean;
  loading?: boolean;
  arrow?: boolean;
  dialogTitle?: ReactNode;
  dialogButton?: ReactNode;
  ref?: RefObject<PickerRef | null>;
  'aria-label'?: string;
};

export const Picker = ({
  className,
  modal: _modal,
  root: _root,
  size,
  error,
  disabled,
  loading,
  arrow,
  ref,
  block,
  dialogTitle,
  dialogButton,
  'aria-label': ariaLabel,
}: PickerProps) => {
  const [open, setOpen] = useState(false);
  const { t } = useLingui();
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (ref) {
      ref.current = [open, setOpen];
    }
  }, [open, ref]);

  const modal = <div className="">{_modal}</div>;

  const rootClassName = cn(
    block ? 'flex' : 'inline-flex',
    'items-center overflow-hidden gap-2 shrink-0 relative',
    'border border-border-disabled',
    open && [error ? 'border-content-negative' : 'border-content-link'],
    error && ['border-content-negative'],
    disabled || loading
      ? 'cursor-not-allowed bg-background-disabled text-content-tertiary'
      : ['cursor-pointer', !open && 'hover:border-border-tertiary'],
    'transition-colors rounded-[inherit]',
    size === 12 && ['px-3 h-12 text-base rounded-xl'],
    size === 10 && ['px-2 h-10 text-sm rounded-lg'],
    size === 8 && ['px-1 h-8 text-xs rounded-lg'],
    className,
  );

  const rootAttrs = {
    ...(open && {
      'data-focus': true,
    }),
    ...(error && {
      'data-error': true,
    }),
    ...((disabled || loading) && {
      'data-disabled': true,
    }),
    'aria-label': ariaLabel,
  };

  const rootContent = (
    <>
      <div className="grow flex justify-start items-center">{_root}</div>
      {loading ? (
        <Spinner className="size-4 opacity-50 me-1" />
      ) : (
        arrow !== false && (
          <ArrowDown2
            size={16}
            className={cn('shrink-0 opacity-50 me-1', open && 'rotate-180')}
          />
        )
      )}
    </>
  );

  const isDisabled = disabled || loading;

  useEffect(() => {
    if (isDisabled && open) {
      setOpen(false);
    }
  }, [isDisabled, open]);

  return isDesktop ? (
    <PopoverRoot
      onOpenChange={(newOpen) => {
        if (!isDisabled) {
          setOpen(newOpen);
        }
      }}
      open={isDisabled ? false : open}
    >
      <PopoverTrigger
        className={cn(rootClassName, isDisabled && 'pointer-events-none')}
        {...rootAttrs}
      >
        {rootContent}
        <Ripple />
      </PopoverTrigger>
      <PopoverContent align="start">{modal}</PopoverContent>
    </PopoverRoot>
  ) : (
    <>
      <button
        className={rootClassName}
        {...rootAttrs}
        type="button"
        disabled={isDisabled}
        onClick={() => {
          if (!isDisabled) {
            setOpen(true);
          }
        }}
      >
        {rootContent}
      </button>
      <Dialog
        position="bottom"
        open={open}
        onClose={() => setOpen(false)}
        header={dialogTitle}
        className="!px-0 !pt-0 !pb-2"
        footer={
          <Button
            variant="ghost"
            size={12}
            block
            className="w-full"
            onClick={() => setOpen(false)}
          >
            {dialogButton ?? t`Close`}
          </Button>
        }
      >
        {modal}
      </Dialog>
    </>
  );
};
